import { ADMIN_MESSAGE_FORMATS } from '../../../../constants/messageFormats';

const TIMETABLE_MEMO_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO,
  ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO_ADMIN,
  'timetable_memo',
  'timetable_memo_admin',
]);

const TIMETABLE_ANNOUNCEMENT_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT,
  ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT_ADMIN,
  'timetable_announcement',
  'timetable_announcement_admin',
]);

const EVENT_NOTE_FORMATS = new Set([
  ADMIN_MESSAGE_FORMATS.NOTE_CREATE,
  ADMIN_MESSAGE_FORMATS.NOTE_UPDATE,
  ADMIN_MESSAGE_FORMATS.NOTE_DELETE,
  'note_create',
  'note_update',
  'note_delete',
]);

const normalizeMessageFormat = (message) =>
  String(message?.message_format || '').trim().toLowerCase();

const LEGACY_EVENT_IMAGES_ADDED_CONTENTS = new Set([
  'イベント画像が追加されました。',
  'イベント画像が追加されました',
]);

export const EVENT_FINISHED_CHAT_TITLE = 'イベント終了のお知らせ';
export const EVENT_FINISHED_CHAT_PREVIEW =
  'イベントが終了しました。契約内容により、まだ請求できない場合があります。';

export const isEventTimetableMemoMessage = (message) => {
  if (!message) return false;
  return TIMETABLE_MEMO_FORMATS.has(message.message_format);
};

export const isEventTimetableAnnouncementMessage = (message) => {
  if (!message) return false;
  return TIMETABLE_ANNOUNCEMENT_FORMATS.has(message.message_format);
};

export const isEventTimetableRuntimeMessage = (message) =>
  isEventTimetableMemoMessage(message) || isEventTimetableAnnouncementMessage(message);

export const isEventNoteMessage = (message) => {
  if (!message) return false;
  const format = normalizeMessageFormat(message);
  if (EVENT_NOTE_FORMATS.has(format)) return true;
  return !format && Boolean(message.note_id);
};

export const isEventNoteCreateMessage = (message) => {
  if (!message) return false;
  const format = normalizeMessageFormat(message);
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_CREATE || format === 'note_create') return true;
  if (format || !message.note_id) return false;
  const content = String(message.content || '');
  return content.includes('ノート') && content.includes('作成');
};

export const isEventNoteUpdateMessage = (message) => {
  if (!message) return false;
  const format = normalizeMessageFormat(message);
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_UPDATE || format === 'note_update') return true;
  if (format || !message.note_id) return false;
  const content = String(message.content || '');
  return content.includes('ノート') && content.includes('修正');
};

export const isEventNoteDeleteMessage = (message) => {
  if (!message) return false;
  const format = normalizeMessageFormat(message);
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_DELETE || format === 'note_delete') return true;
  if (format || !message.note_id) return false;
  const content = String(message.content || '');
  return content.includes('ノート') && content.includes('削除');
};

export const isEventTimetableCreatedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.TIMETABLE_CREATED || format === 'timetable_created';
};

export const isEventTimetableUpdatedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.TIMETABLE_UPDATED || format === 'timetable_updated';
};

export const isEventMaterialSharedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.EVENT_MATERIAL_SHARED || format === 'event_material_shared';
};

export const isEventImagesAddedMessage = (message) => {
  if (!message) return false;
  const format = normalizeMessageFormat(message);
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_IMAGES_ADDED || format === 'event_images_added') return true;
  if (format !== ADMIN_MESSAGE_FORMATS.TEXT) return false;
  if (String(message.message_type || '').trim().toLowerCase() !== 'system_message') return false;
  return LEGACY_EVENT_IMAGES_ADDED_CONTENTS.has(String(message.content || '').trim());
};

export const isEventInfoUpdatedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED || format === 'event_info_updated';
};

export const isEventInfoPublishedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_PUBLISHED || format === 'event_info_published';
};

export const isEventMembersPublishedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.EVENT_MEMBERS_PUBLISHED || format === 'event_members_published';
};

export const isStageSeatingSharedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  if (format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_STAGE_SEATING_SHARED || format === 'announce_stage_seating_shared') return true;
  if (format === ADMIN_MESSAGE_FORMATS.STAGE_SEATING_SHARED || format === 'stage_seating_shared') return true;
  if (format !== ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED && format !== 'event_info_updated') return false;
  return String(message.content || message.last_message_preview || '').includes('ステージシーティング');
};

export const isEventFinishedMessage = (message) => {
  if (!message) return false;
  const format = message.message_format;
  return format === ADMIN_MESSAGE_FORMATS.EVENT_FINISHED || format === 'event_finished';
};

export const isEventSimpleAnnouncementMessage = (message) => (
  isEventTimetableCreatedMessage(message) ||
  isEventTimetableUpdatedMessage(message) ||
  isEventMaterialSharedMessage(message) ||
  isEventImagesAddedMessage(message) ||
  isEventInfoUpdatedMessage(message) ||
  isEventInfoPublishedMessage(message) ||
  isEventMembersPublishedMessage(message) ||
  isStageSeatingSharedMessage(message) ||
  isEventFinishedMessage(message)
);

export const isEventAnnouncementMessage = (message) =>
  isEventNoteMessage(message) ||
  isEventSimpleAnnouncementMessage(message) ||
  isEventTimetableRuntimeMessage(message);

export const resolveEventSidebarPreview = (eventRow = {}) => {
  const format = eventRow.last_message_format;
  if (
    format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_STAGE_SEATING_SHARED ||
    format === 'announce_stage_seating_shared' ||
    format === ADMIN_MESSAGE_FORMATS.STAGE_SEATING_SHARED ||
    format === 'stage_seating_shared'
  ) {
    return 'ステージシーティングが公開されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_CREATE || format === 'note_create') return 'ノートが作成されました。';
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_UPDATE || format === 'note_update') return 'ノートが修正されました。';
  if (format === ADMIN_MESSAGE_FORMATS.NOTE_DELETE || format === 'note_delete') return 'ノートが削除されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_CREATED || format === 'timetable_created') return 'タイムテーブルが作成されました。';
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_UPDATED || format === 'timetable_updated') return 'タイムテーブルが更新されました。';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_MATERIAL_SHARED || format === 'event_material_shared') {
    return '共有資料がアーティストに共有されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_IMAGES_ADDED || format === 'event_images_added') {
    return 'イベントアルバムが共有されました。';
  }
  if (
    (format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED || format === 'event_info_updated') &&
    String(eventRow.content || eventRow.last_message_preview || '').includes('ステージシーティング')
  ) {
    return 'ステージシーティングが公開されました。';
  }
  const preview = String(eventRow.last_message_preview || '').trim();
  if (preview) return preview;
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED || format === 'event_info_updated') {
    return 'イベントの情報が更新または追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_INFO_PUBLISHED || format === 'event_info_published') {
    return '情報公開がされました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_MEMBERS_PUBLISHED || format === 'event_members_published') {
    return '参加メンバーが公開されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO || format === 'timetable_memo') {
    return 'タイムテーブルの行メモが追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_MEMO_ADMIN || format === 'timetable_memo_admin') {
    return 'タイムテーブルの行メモ（管理者のみ）が追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT || format === 'timetable_announcement') {
    return 'タイムテーブルのお知らせが追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.TIMETABLE_ANNOUNCEMENT_ADMIN || format === 'timetable_announcement_admin') {
    return 'タイムテーブルのお知らせ（管理者のみ）が追加されました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || format === 'event_cancelled') {
    return 'イベントが中止となりました。';
  }
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_FINISHED || format === 'event_finished') {
    return EVENT_FINISHED_CHAT_PREVIEW;
  }
  if (format === ADMIN_MESSAGE_FORMATS.FILE || format === 'file') return 'ファイルが届いています。';
  if (format === ADMIN_MESSAGE_FORMATS.IMAGE || format === 'image') return '画像が届いています。';
  return '';
};

export const resolveEventAnnouncementIconClass = (message) => {
  const format = String(message?.message_format || '').trim();
  if (isEventNoteMessage(message)) return 'fa-note-sticky';
  if (isEventTimetableMemoMessage(message)) return 'fa-pen-to-square';
  if (isEventTimetableAnnouncementMessage(message)) return 'fa-bullhorn';
  if (isEventTimetableCreatedMessage(message) || isEventTimetableUpdatedMessage(message)) {
    return 'fa-calendar-days';
  }
  if (isEventMaterialSharedMessage(message)) return 'fa-file-lines';
  if (isEventImagesAddedMessage(message)) return 'fa-images';
  if (isEventInfoUpdatedMessage(message)) return 'fa-circle-info';
  if (isEventInfoPublishedMessage(message)) return 'fa-earth-asia';
  if (isEventMembersPublishedMessage(message)) return 'fa-people-group';
  if (isStageSeatingSharedMessage(message)) return 'fa-chair';
  if (format === ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED || format === 'event_cancelled') return 'fa-ban';
  if (isEventFinishedMessage(message)) return 'fa-flag-checkered';
  return 'fa-bullhorn';
};
